import React, { useState, useCallback } from 'react';
import { Edit2, Check, X, Building2, Briefcase, User } from 'lucide-react';
import { apiService, UserProfile } from '../services/api';

interface UserProfileEditorProps {
  profile: UserProfile;
  onUpdate: () => void | Promise<void>;
}

const MAX_LENGTH = 100;

export const UserProfileEditor: React.FC<UserProfileEditorProps> = ({
  profile,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(profile.name || '');
  const [editCompany, setEditCompany] = useState(profile.company || '');
  const [editRole, setEditRole] = useState(profile.role || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setError(null);
    
    try {
      const trimmedName = editName.trim();
      const trimmedCompany = editCompany.trim();
      const trimmedRole = editRole.trim();
      
      if (trimmedName.length > MAX_LENGTH) {
        throw new Error(`Name must be ${MAX_LENGTH} characters or less`);
      }
      
      if (trimmedCompany.length > MAX_LENGTH) {
        throw new Error(`Company must be ${MAX_LENGTH} characters or less`);
      }
      if (trimmedRole.length > MAX_LENGTH) {
        throw new Error(`Role must be ${MAX_LENGTH} characters or less`);
      }
      
      await apiService.updateUserProfile({
        name: trimmedName || undefined,
        company: trimmedCompany || undefined,
        role: trimmedRole || undefined
      });
      
      await onUpdate();
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  }, [editName, editCompany, editRole, onUpdate]);

  const handleCancel = useCallback(() => {
    setEditName(profile.name || '');
    setEditCompany(profile.company || '');
    setEditRole(profile.role || '');
    setError(null);
    setIsEditing(false);
  }, [profile]);

  const handleEdit = useCallback(() => {
    setEditName(profile.name || '');
    setEditCompany(profile.company || '');
    setEditRole(profile.role || '');
    setError(null);
    setIsEditing(true);
  }, [profile]);

  const hasProfile = profile.name || profile.company || profile.role;

  if (isEditing) {
    return (
      <div className="user-profile-editor user-profile-editor--editing">
        <div className="user-profile-editor__header">
          <h3 className="user-profile-editor__title">Your Profile</h3>
        </div>
        
        <div className="user-profile-editor__fields">
          <div className="user-profile-editor__field">
            <div className="user-profile-editor__field-icon">
              <User size={14} />
            </div>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Add your name"
              maxLength={MAX_LENGTH}
              disabled={isSaving}
              className="user-profile-editor__input"
            />
          </div>

          <div className="user-profile-editor__field">
            <div className="user-profile-editor__field-icon">
              <Building2 size={14} />
            </div>
            <input
              type="text"
              value={editCompany}
              onChange={(e) => setEditCompany(e.target.value)}
              placeholder="Add your company"
              maxLength={MAX_LENGTH}
              disabled={isSaving}
              className="user-profile-editor__input"
            />
          </div>
          
          <div className="user-profile-editor__field">
            <div className="user-profile-editor__field-icon">
              <Briefcase size={14} />
            </div>
            <input
              type="text"
              value={editRole}
              onChange={(e) => setEditRole(e.target.value)}
              placeholder="Add your role"
              maxLength={MAX_LENGTH}
              disabled={isSaving}
              className="user-profile-editor__input"
            />
          </div>
        </div>

        {error && (
          <div className="user-profile-editor__error">
            {error}
          </div>
        )}

        <div className="user-profile-editor__char-count">
          Max {MAX_LENGTH} characters per field
        </div>

        <div className="user-profile-editor__actions">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="user-profile-editor__btn user-profile-editor__btn--save"
          >
            <Check size={14} />
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving}
            className="user-profile-editor__btn user-profile-editor__btn--cancel"
          >
            <X size={14} />
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile-editor">
      <div className="user-profile-editor__header">
        <h3 className="user-profile-editor__title">Your Profile</h3>
        <button
          type="button"
          onClick={handleEdit}
          className="user-profile-editor__edit-btn"
          aria-label="Edit profile"
        >
          <Edit2 size={14} />
        </button>
      </div>
      
      <div className="user-profile-editor__display">
        <div className="user-profile-editor__display-row">
          <div className="user-profile-editor__display-icon">
            <User size={14} />
          </div>
          <span className={`user-profile-editor__display-value ${!profile.name ? 'user-profile-editor__display-value--placeholder' : ''}`}>
            {profile.name || 'Add your name'}
          </span>
        </div>

        <div className="user-profile-editor__display-row">
          <div className="user-profile-editor__display-icon">
            <Building2 size={14} />
          </div>
          <span className={`user-profile-editor__display-value ${!profile.company ? 'user-profile-editor__display-value--placeholder' : ''}`}>
            {profile.company || 'Add your company'}
          </span>
        </div>
        
        <div className="user-profile-editor__display-row">
          <div className="user-profile-editor__display-icon">
            <Briefcase size={14} />
          </div>
          <span className={`user-profile-editor__display-value ${!profile.role ? 'user-profile-editor__display-value--placeholder' : ''}`}>
            {profile.role || 'Add your role'}
          </span>
        </div>
      </div>

      {hasProfile && (
        <div className="user-profile-editor__hint user-profile-editor__hint--success">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          Used in Brief Builder context
        </div>
      )}

      {!hasProfile && (
        <div className="user-profile-editor__hint user-profile-editor__hint--info">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          Add these details to skip repetitive questions
        </div>
      )}
    </div>
  );
};
